import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { RootState } from '@/redux/store';
import {
  useDeleteCartItemMutation,
  useGetCartQuery,
  useUpdateCartItemMutation,
  usePutCartItemsMutation,
  usePostCartItemMutation
} from '@/redux/api/client/cart';
import { CartViewModel, OrderLineViewModel } from '@/@types/client-api';
import { closeCartPanel } from '@/redux/slices/cartSlice';
import useToast from '@/hooks/useToast';
import { useGetContentByFriendlyUrlQuery } from '@/redux/api/contentDeliveryAPI';
import type { CartPage } from '@/@types/content';
import { usePromoCode } from './usePromoCode';
import { useEffect, useState, useRef } from 'react';
import { formatPrice } from '@/utils/helpers';
import { useSearchParamsStable } from '@/hooks/useSearchParams';
import { readPriorityCodeFromPLIPromoCookie } from '@/hooks/usePLIPromoCookie';

interface UseCartReturn {
  cartData: CartViewModel | undefined;
  isLoading: boolean;
  isUpdating: boolean;
  isCartEmpty: boolean;
  isShippingRequired: boolean;
  subTotal: string;
  cartPageData?: CartPage;
  cartPath?: string;
  checkout: {
    buttonProps: {
      label: string;
      href: string;
      onClick?: () => void;
    };
  };
  deleteCartItem: (item: OrderLineViewModel) => void;
  applyPromoCode: (promoCode: string) => void;
  isApplyingPromoCode?: boolean;
  promoCodeInputErrorMessage?: string;
  removePromoCode: (item: OrderLineViewModel) => void;
  selectedQuantities: Record<string, string>;
  handleQuantityChange: (
    item: OrderLineViewModel,
    newQuantity: string,
    shouldUpdateCartItem: boolean
  ) => void;
  isError: boolean;
  didCartBecomeEmpty: boolean;
}

export const useCart = (): UseCartReturn => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccessMessage, showFailMessage } = useToast();
  const { searchParams } = useSearchParamsStable();

  const siteLabels = useSelector((state: RootState) => state.page.siteLabels);
  const { data: cartData, isLoading, isFetching, isError } = useGetCartQuery();
  const [deleteCartItem, { isLoading: isDeleting }] = useDeleteCartItemMutation();
  const [updateCartItem, { isLoading: isUpdatingItem }] = useUpdateCartItemMutation();
  const {
    handleApplyPromoCode,
    isLoading: isApplyingPromoCode,
    inputErrorMessage: promoCodeInputErrorMessage
  } = usePromoCode();
  const [addCartItem, { isLoading: isAdding }] = usePostCartItemMutation();
  const [putCartItems, { isLoading: isPutting }] = usePutCartItemsMutation();

  const isUpdating =
    isFetching || isDeleting || isUpdatingItem || isAdding || isApplyingPromoCode || isPutting;
  const addItemsProcessed = useRef(false);

  const isCartEmpty = cartData?.orderLines?.length === 0;

  const [didCartBecomeEmpty, setDidCartBecomeEmpty] = useState(false);
  const previousCartItemCount = useRef<number | undefined>(undefined);
  const currentCartItemCount = cartData?.orderLines?.length ?? 0;

  useEffect(() => {
    if (
      currentCartItemCount === 0 &&
      previousCartItemCount.current !== undefined &&
      previousCartItemCount.current > 0
    ) {
      setDidCartBecomeEmpty(true);
    } else if (currentCartItemCount > 0 && didCartBecomeEmpty) {
      setDidCartBecomeEmpty(false);
    }

    previousCartItemCount.current = currentCartItemCount;
  }, [currentCartItemCount, didCartBecomeEmpty]);

  const cartPath = siteLabels?.cartUrl
    ? new URL(siteLabels.cartUrl, window.location.origin).pathname
    : undefined;

  const { data: cartPageData } = useGetContentByFriendlyUrlQuery(
    { friendlyUrl: cartPath!, userId: 'ANON' },
    { skip: !cartPath }
  );

  const formatSubTotal = (): string => {
    if (cartData?.hasErrors) return '--';

    return formatPrice(cartData?.subTotal ?? 0) || '0.00';
  };

  const handleCheckout = () => {
    dispatch(closeCartPanel());
  };

  const [selectedQuantities, setSelectedQuantities] = useState<Record<string, string>>({});

  useEffect(() => {
    if (cartData) {
      const initialQuantities: Record<string, string> = {};
      cartData.orderLines?.forEach(item => {
        initialQuantities[item.code!] = item.quantity?.toString() || '1';
      });
      setSelectedQuantities(initialQuantities);
    }
  }, [cartData]);

  useEffect(() => {
    const addItemsParam = searchParams.get('addItems');
    const priorityCodeParam = searchParams.get('priorityCode');

    if (addItemsParam && !addItemsProcessed.current && !isLoading) {
      addItemsProcessed.current = true;
      const itemCodes = addItemsParam
        .split(',')
        .map(code => code.trim())
        .filter(code => code);

      if (itemCodes.length === 0) {
        return;
      }

      const addItems = async () => {
        try {
          const priorityCode = priorityCodeParam || readPriorityCodeFromPLIPromoCookie();
          const orderLines: OrderLineViewModel[] = itemCodes.map(itemCode => ({
            code: itemCode,
            quantity: 1,
            priorityCode: priorityCode?.replaceAll('_', ' ')
          }));

          await putCartItems(orderLines).unwrap();
        } catch (error) {
          console.error('Error adding items to cart:', error);
        }
      };

      addItems();
      navigate(location.pathname, { replace: true });
    }
  }, [searchParams, putCartItems, isLoading, navigate, location.pathname]);

  const handleQuantityChange = (
    item: OrderLineViewModel,
    newQuantity: string,
    shouldUpdateCartItem: boolean
  ) => {
    setSelectedQuantities(prev => ({ ...prev, [item.code!]: newQuantity }));

    if (shouldUpdateCartItem) {
      updateCartItem([
        { code: item.code!, quantity: Number(newQuantity), priorityCode: item.priorityCode! }
      ]);
    }
  };

  const handleDeleteCartItem = async (item: OrderLineViewModel) => {
    try {
      const request: OrderLineViewModel[] = [{ code: item.code }];
      await deleteCartItem(request).unwrap();

      const itemDeletedMessage = (
        <span className="z-toast">
          Item removed{' '}
          <button
            className="pointer-events-auto underline"
            onClick={() =>
              addCartItem({
                code: item.code,
                quantity: item.quantity,
                priorityCode: item.priorityCode
              })
            }
          >
            Undo
          </button>
        </span>
      );
      showSuccessMessage(itemDeletedMessage);
    } catch (error) {
      showFailMessage('Failed to delete the item');
      console.error('Error deleting cart item:', error);
    }
  };

  const handleRemovePromoCode = async (item: OrderLineViewModel) => {
    try {
      const request: OrderLineViewModel[] = [
        { code: item.code, quantity: item.quantity, priorityCode: '' }
      ];
      await updateCartItem(request).unwrap();

      const promoCodeRemovedMessage = (
        <span className="z-toast">
          Promo code removed.{' '}
          <button
            className="pointer-events-auto underline"
            onClick={() => updateCartItem([{ ...request[0], priorityCode: item.priorityCode }])}
          >
            Undo
          </button>
        </span>
      );

      showSuccessMessage(promoCodeRemovedMessage);
    } catch (error) {
      showFailMessage('Failed to remove the promo code');
      console.error('Error removing promo code:', error);
    }
  };

  const checkoutButtonProps = {
    label: 'Checkout',
    href: siteLabels?.checkoutPageReference?.url || '',
    color: 'black',
    onClick: handleCheckout
  };

  return {
    cartData,
    isLoading,
    isUpdating,
    isShippingRequired: cartData?.orderLines?.some(x => x.isStockItem ?? false) ?? false,
    subTotal: formatSubTotal(),
    cartPageData,
    checkout: {
      buttonProps: checkoutButtonProps
    },
    deleteCartItem: handleDeleteCartItem,
    applyPromoCode: handleApplyPromoCode,
    removePromoCode: handleRemovePromoCode,
    isApplyingPromoCode,
    promoCodeInputErrorMessage,
    selectedQuantities,
    handleQuantityChange,
    isCartEmpty,
    cartPath,
    isError,
    didCartBecomeEmpty
  };
};
